import { formatGuardBits, getLastConditionData, getLastTransportCondition } from 'app/types';
import { isActivity2216Red } from 'app/utils/activity2216';
function getTransportDeviceType(item) {
    return config.transportDeviceTypes[item.device_type];
}
function isEmptySortValue(item, columnName) {
    const deviceType = getTransportDeviceType(item);
    switch (columnName) {
        case 'number':
            return item.number == null;
        case 'geofence':
            return item.geofence_id === config.conditions.geofence_id.default;
        case 'parking_time':
        case 'sensor_count':
            return deviceType !== 'galileosky' || item[columnName] === config.conditions[columnName].default;
        case 'total_mileage_avg':
        case 'guard_status_sum':
            return deviceType !== 'galileosky' || item[columnName] == null;
        case 'signal_off':
            return (deviceType !== 'galileosky' && deviceType !== 'onlyWialon') || item.signal_off == null;
        case 'last_activity_date':
            return item.last_activity_date == null;
        default:
            return false;
    }
}
function compareFilledTransportItems(a, b, transportSortSelectedItem, datetimeInterval) {
    const { name, order } = transportSortSelectedItem;
    switch (name) {
        case 'name':
            return order === 'DESC' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
        case 'number':
            return order === 'DESC' ? String(b.number).localeCompare(String(a.number)) : String(a.number).localeCompare(String(b.number));
        case 'condition':
            return order === 'DESC' ? moment(a.condition).valueOf() - moment(b.condition).valueOf() : moment(b.condition).valueOf() - moment(a.condition).valueOf();
        case 'geofence':
            return order === 'DESC' ? (b.geofence_name || '').localeCompare(a.geofence_name || '') || b.name.localeCompare(a.name) : (a.geofence_name || '').localeCompare(b.geofence_name || '') || a.name.localeCompare(b.name);
        case 'parking_time':
        case 'sensor_count':
        case 'signal_off':
        case 'guard_status_sum':
        case 'total_mileage_avg':
            return order === 'DESC' ? b[name] - a[name] : a[name] - b[name];
        case 'last_activity_date': {
            const aMinutes = Math.round(moment(datetimeInterval).diff(moment(a.last_activity_date)) / 1000 / 60);
            const bMinutes = Math.round(moment(datetimeInterval).diff(moment(b.last_activity_date)) / 1000 / 60);
            return order === 'DESC' ? bMinutes - aMinutes : aMinutes - bMinutes;
        }
        default:
            return 0;
    }
}
function sortTransportItems(items, transportSortSelectedItem, datetimeInterval) {
    const decorated = items.map((item, index) => ({
        item,
        index,
        empty: isEmptySortValue(item, transportSortSelectedItem.name),
    }));
    decorated.sort((a, b) => {
        if (a.empty !== b.empty) {
            return a.empty ? 1 : -1;
        }
        if (a.empty) {
            return a.index - b.index;
        }
        const result = compareFilledTransportItems(a.item, b.item, transportSortSelectedItem, datetimeInterval);
        return result !== 0 ? result : a.index - b.index;
    });
    return decorated.map((entry) => entry.item);
}
function enrichTransportItem(sourceValue, datetimeInterval) {
    var _a, _b, _c, _d, _e;
    const currentValue = Object.assign(Object.assign({}, sourceValue), { datetime: '', geofence_name: 'NA', geofence_id: config.conditions.geofence_id.default, guard_status: config.conditions.guard_status.default, parking_time: config.conditions.parking_time.default, sensor_count: config.conditions.sensor_count.default, guard_status_sum: null, signal_off: null, condition: null });
    if (((_b = (_a = currentValue.conditions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0) {
        return currentValue;
    }
    const lastCondition = getLastTransportCondition(currentValue.conditions);
    const lastData = getLastConditionData(currentValue.conditions);
    if (!lastCondition || !lastData) {
        return currentValue;
    }
    currentValue.datetime = lastCondition.datetime;
    currentValue.geofence_id = lastData.geofence_id;
    currentValue.geofence_name = lastData.geofence_name;
    currentValue.guard_status = formatGuardBits(lastData.guard_status);
    currentValue.guard_status_sum = config.numberOfTank
        .map((number) => lastData[`dut${number}`])
        .filter((number) => number !== config.conditions.dut.default)
        .reduce((a, b) => a + b, 0);
    currentValue.total_mileage_avg = (_c = lastData.total_mileage_avg) !== null && _c !== void 0 ? _c : null;
    currentValue.parking_time = (_d = lastData.parking_time) !== null && _d !== void 0 ? _d : config.conditions.parking_time.default;
    currentValue.sensor_count = (_e = lastData.sensor_count) !== null && _e !== void 0 ? _e : config.conditions.sensor_count.default;
    currentValue.signal_off = Math.max(0, moment(datetimeInterval).diff(moment(lastCondition.receive_date).endOf('minute')));
    return currentValue;
}
function matchesTransportFilters(currentValue, transportFilterCheckedItems, datetimeInterval) {
    var _a, _b;
    const matchesActivityFilter = () => {
        if (transportFilterCheckedItems.indexOf('activity') === -1) {
            return false;
        }
        const hasWialon2216Device = currentValue.wialon_type === 2 || Boolean(currentValue.wialon_imei);
        if (!hasWialon2216Device) {
            return false;
        }
        return isActivity2216Red(datetimeInterval, currentValue.last_motion_galileosky_date, currentValue.last_activity_date);
    };
    // Без состояний остальные фильтры не к чему применить.
    // «2216» — только красный признак; прочие галочки сохраняют прежний показ «пустых» ТС.
    if (((_b = (_a = currentValue.conditions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0) {
        if (matchesActivityFilter()) {
            return true;
        }
        return transportFilterCheckedItems.some((name) => name !== 'activity');
    }
    const lastCondition = getLastTransportCondition(currentValue.conditions);
    const lastData = getLastConditionData(currentValue.conditions);
    if (!lastCondition || !lastData) {
        if (matchesActivityFilter()) {
            return true;
        }
        return transportFilterCheckedItems.some((name) => name !== 'activity');
    }
    const guard_out = formatGuardBits(lastData.guard_out);
    const guard_status = formatGuardBits(lastData.guard_status);
    if (transportFilterCheckedItems.indexOf('guard') !== -1) {
        if (lastData.guard_out !== config.conditions.guard_out.default && guard_out[1] === '1') {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('alarm') !== -1) {
        if (lastData.guard_status !== config.conditions.guard_status.default && guard_status[1] === '1') {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('drain') !== -1) {
        if (lastData.geofence_name !== 'NA' && lastData.geofence_type === 1 && guard_status[2] === '1') {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('pouring') !== -1) {
        if (lastData.geofence_name !== 'NA' && lastData.geofence_type === 3) {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('parking') !== -1) {
        if (lastData.is_parked === 1) {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('speed') !== -1) {
        if (lastData.is_parked === 0) {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('signal') !== -1) {
        const diff = Math.max(0, moment(datetimeInterval).diff(moment(lastCondition.receive_date).endOf('minute')));
        if (diff > config.signalOffTimeout[0].interval[1]) {
            return true;
        }
    }
    if (transportFilterCheckedItems.indexOf('parking_time') !== -1) {
        if (lastData.geofence_name === 'NA' && lastData.parking_overtime === 1 && (lastData.parking_time || 0) <= 21600) {
            return true;
        }
    }
    return matchesActivityFilter();
}
/** Фильтрация и сортировка списка транспорта на карте. */
export function filterAndSortTransportItems(transportItems, transportFilterCheckedItems, transportSortSelectedItem, datetimeInterval) {
    const visibleFiltersCount = config.mapTransportFilters.filter((filter) => !filter.hidden).length;
    const items = transportItems.reduce((accumulator, sourceValue) => {
        var _a, _b;
        if (accumulator.findIndex((item) => item.id === sourceValue.id) !== -1) {
            return accumulator;
        }
        const currentValue = enrichTransportItem(sourceValue, datetimeInterval);
        const showAll = transportFilterCheckedItems.length >= visibleFiltersCount;
        if (((_b = (_a = currentValue.conditions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0) {
            if (showAll || matchesTransportFilters(currentValue, transportFilterCheckedItems, datetimeInterval)) {
                return [...accumulator, currentValue];
            }
            return accumulator;
        }
        const lastCondition = getLastTransportCondition(currentValue.conditions);
        const matchedFilterDates = ((lastCondition === null || lastCondition === void 0 ? void 0 : lastCondition.filters) || []).filter((filter) => transportFilterCheckedItems.indexOf(filter.name) !== -1).map((filter) => new Date(filter.datetime).getTime());
        if (matchedFilterDates.length > 0) {
            currentValue.condition = Math.max(...matchedFilterDates);
        }
        if (showAll || matchesTransportFilters(currentValue, transportFilterCheckedItems, datetimeInterval)) {
            return [...accumulator, currentValue];
        }
        return accumulator;
    }, []);
    return sortTransportItems(items, transportSortSelectedItem, datetimeInterval);
}
